import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { BsLayoutThreeColumns } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import FormField from '#globalComponents/Forms/FormField';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { COLUMN_TYPE_LABELS } from '../../constants/recipientFields';
import {
	useCreateCustomColumn,
	usePreviewCustomColumn,
	useStartColumnFill,
	useUpdateCustomColumn,
} from '../../models/mutations';

import ColumnDefinitionFields from './ColumnDefinitionFields';
import PreviewResults from './PreviewResults';

const EMPTY_DRAFT = { name: '', type: 'ai', prompt: '', template: '', fallback: '', useWebsite: false };

const draftFrom = (column) =>
	column
		? {
				name: column.name,
				type: column.type,
				prompt: column.prompt ?? '',
				template: column.template ?? '',
				fallback: column.fallback ?? '',
				useWebsite: column.useWebsite,
			}
		: EMPTY_DRAFT;

const definitionOf = (draft) =>
	draft.type === 'ai'
		? { prompt: draft.prompt.trim(), useWebsite: draft.useWebsite }
		: { template: draft.template.trim() };

/**
 * Create or edit a custom column, test it on up to five rows of the list, and (for ai columns)
 * start filling the list right away.
 */
const CustomColumnModal = ({
	isOpen,
	onClose,
	column,
	listId,
	table,
	previewScope,
	previewCount,
	sampleLabels,
	fillScope,
	fillCount,
	onFillStarted,
}) => {
	const [draft, setDraft] = useState(EMPTY_DRAFT);
	const createMutation = useCreateCustomColumn();
	const updateMutation = useUpdateCustomColumn();
	const previewMutation = usePreviewCustomColumn();
	const fillMutation = useStartColumnFill();

	useEffect(() => {
		if (!isOpen) return;
		setDraft(draftFrom(column));
		previewMutation.reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the dialog opens
	}, [isOpen, column]);

	const change = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
	const definition = definitionOf(draft);
	const hasDefinition = Boolean(definition.prompt || definition.template);
	const canSave = Boolean(draft.name.trim()) && hasDefinition;
	const isSaving = createMutation.isPending || updateMutation.isPending || fillMutation.isPending;
	const labelFor = (row) => row.label ?? sampleLabels[row.crmContactId ?? row.crmCompanyId] ?? '';

	const runPreview = () =>
		previewMutation.mutate({
			type: draft.type,
			...definition,
			fallback: draft.fallback.trim() || null,
			scope: { table, contextId: listId, ...previewScope },
		});

	const save = async ({ andFill }) => {
		const fields = { name: draft.name.trim(), fallback: draft.fallback.trim() || null, ...definition };
		const saved = column
			? await updateMutation.mutateAsync({ id: column.id, updates: fields })
			: await createMutation.mutateAsync({ type: draft.type, ...fields });
		const fill = andFill
			? await fillMutation.mutateAsync({
					id: saved.id,
					scope: { table, contextId: listId, ...fillScope },
					mode: 'missing',
				})
			: null;
		onClose();
		if (fill) onFillStarted(saved, fill);
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="l" closeOnBackdrop={!isSaving}>
			<Panel.Header icon={BsLayoutThreeColumns} onClose={onClose}>
				{column ? `Spalte „${column.name}“ bearbeiten` : 'Neue Spalte'}
			</Panel.Header>
			<Panel.Body className="space-y-4">
				<FormField
					label="Name"
					hint={column ? `Variable: {${column.key}}` : 'Daraus entsteht die Variable für E-Mails und Briefe.'}
				>
					<TextInput value={draft.name} onChange={(e) => change({ name: e.target.value })} maxLength={60} />
				</FormField>

				<FormField label="Art">
					<div className="flex gap-2">
						{Object.entries(COLUMN_TYPE_LABELS).map(([type, label]) => (
							<Button
								key={type}
								size="sm"
								variant={draft.type === type ? 'solid' : 'outline'}
								disabled={Boolean(column)}
								onClick={() => change({ type })}
							>
								{label}
							</Button>
						))}
					</div>
				</FormField>

				<ColumnDefinitionFields draft={draft} onChange={change} />

				<FormField
					label="Ersatzwert (optional)"
					hint="Wird genutzt, wenn kein Wert entsteht. Ohne Ersatzwert werden solche Empfänger beim Versand übersprungen."
				>
					<TextInput value={draft.fallback} onChange={(e) => change({ fallback: e.target.value })} />
				</FormField>

				<div className="space-y-2">
					<Button
						variant="outline"
						size="sm"
						onClick={runPreview}
						loading={previewMutation.isPending}
						disabled={!hasDefinition || previewCount === 0}
					>
						An {previewCount} Zeilen testen
					</Button>
					{previewMutation.data && <PreviewResults rows={previewMutation.data.rows} labelFor={labelFor} />}
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={onClose} disabled={isSaving}>
					Abbrechen
				</Button>
				<Button
					variant={draft.type === 'ai' ? 'outline' : 'solid'}
					onClick={() => save({ andFill: false })}
					disabled={!canSave}
					loading={isSaving}
				>
					Speichern
				</Button>
				{draft.type === 'ai' && (
					<Button
						onClick={() => save({ andFill: true })}
						disabled={!canSave || fillCount === 0}
						loading={isSaving}
					>
						Speichern & {fillCount.toLocaleString('de-DE')} Zeilen befüllen
					</Button>
				)}
			</Panel.Footer>
		</Modal>
	);
};

CustomColumnModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	column: PropTypes.object,
	listId: PropTypes.string.isRequired,
	table: PropTypes.oneOf(['contactListContacts', 'contactListCompanies']).isRequired,
	// Test scope: up to five selected ids, or the tab scope the backend picks test rows from.
	previewScope: PropTypes.object.isRequired,
	previewCount: PropTypes.number.isRequired,
	// Row labels of the current page by recipient id, for rows the backend returns without a label.
	sampleLabels: PropTypes.object.isRequired,
	// Selection scope of the tab ({ ids } | { selectAll, filters }) the fill runs on.
	fillScope: PropTypes.object.isRequired,
	fillCount: PropTypes.number.isRequired,
	// (column, { jobId, total }) after a fill run was queued.
	onFillStarted: PropTypes.func.isRequired,
};

export default CustomColumnModal;
